// src/pages/Accounting/Reconcile/Session/SessionHeader.tsx

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { PALETTE } from "../constants";
import type { Account } from "../types";
import { formatAccountLabel, formatDateLong } from "../utils";

interface SessionHeaderProps {
  account: Account | undefined;
  periodStart: string;
  periodEnd: string;
  onEditInfo: () => void;
  onFinishNow: () => void;
  onSaveForLater: () => void;
  onCloseWithoutSaving: () => void;
  finishEnabled: boolean;
}

export function SessionHeader({
  account,
  periodStart,
  periodEnd,
  onEditInfo,
  onFinishNow,
  onSaveForLater,
  onCloseWithoutSaving,
  finishEnabled,
}: SessionHeaderProps) {
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [dropdownOpen]);

  const currency = account?.currency ?? "CAD";
  const title = account ? formatAccountLabel(account) : "Reconcile";
  const periodLabel = `${formatDateLong(periodStart)} - ${formatDateLong(periodEnd)}`;

  return (
    <div style={wrapStyle}>
      <div style={crumbStyle}>
        <span style={crumbLinkStyle} onClick={() => navigate("/accounting")}>Accounting</span>
        <span style={crumbSepStyle}>/</span>
        <span style={crumbLinkStyle} onClick={() => navigate("/accounting/reconcile")}>Reconcile</span>
        <span style={crumbSepStyle}>/</span>
        <span style={{ color: PALETTE.ink, fontWeight: 600 }}>Session</span>
      </div>

      <div style={titleRowStyle}>
        <div style={{ minWidth: 0 }}>
          <h1 style={titleStyle}>
            {account?.vela && <span style={vMarkStyle}>V</span>}
            {title}
          </h1>
          <div style={subStyle}>
            Statement ending date: {formatDateLong(periodEnd)} &middot; period {periodLabel} &middot; {currency}
          </div>
        </div>

        <div style={actionsStyle} ref={dropdownRef}>
          <button type="button" onClick={onEditInfo} style={btnGhostStyle}>
            Edit info
          </button>
          <button type="button" onClick={() => setDropdownOpen((v) => !v)} style={btnDarkStyle}>
            Save for later
            <ChevronDown size={12} style={{ opacity: 0.75 }} />
          </button>
          {dropdownOpen && (
            <div style={dropdownStyle}>
              <button
                type="button"
                onClick={() => { setDropdownOpen(false); if (finishEnabled) onFinishNow(); }}
                style={{
                  ...dropdownBtnStyle,
                  color: finishEnabled ? PALETTE.ink : PALETTE.ink4,
                  background: finishEnabled ? PALETTE.line : "transparent",
                  fontWeight: finishEnabled ? 600 : 500,
                  cursor: finishEnabled ? "pointer" : "not-allowed",
                }}
                disabled={!finishEnabled}
              >
                Finish now {finishEnabled ? "(balanced)" : "(not balanced yet)"}
              </button>
              <button type="button" onClick={() => { setDropdownOpen(false); onSaveForLater(); }} style={dropdownBtnStyle}>
                Save for later
              </button>
              <button type="button" onClick={() => { setDropdownOpen(false); onCloseWithoutSaving(); }} style={{ ...dropdownBtnStyle, color: PALETTE.redDark }}>
                Close without saving
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const wrapStyle: React.CSSProperties = { background: "#fff", padding: "18px 32px 20px", borderBottom: `1px solid ${PALETTE.line}` };
const crumbStyle: React.CSSProperties = { fontSize: 13, color: PALETTE.ink3, fontWeight: 500, marginBottom: 6 };
const crumbLinkStyle: React.CSSProperties = { color: PALETTE.ink3, cursor: "pointer" };
const crumbSepStyle: React.CSSProperties = { color: PALETTE.ink4, margin: "0 6px" };
const titleRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 24 };
const titleStyle: React.CSSProperties = { fontSize: 26, fontWeight: 800, margin: "0 0 4px", color: PALETTE.ink, letterSpacing: "-0.02em", display: "flex", alignItems: "center", gap: 10 };
const subStyle: React.CSSProperties = { fontSize: 13.5, color: PALETTE.ink3, fontWeight: 500 };
const actionsStyle: React.CSSProperties = { display: "flex", gap: 10, alignItems: "center", flexShrink: 0, position: "relative" };
const btnGhostStyle: React.CSSProperties = { background: "#fff", border: `1px solid ${PALETTE.line}`, color: PALETTE.ink, fontWeight: 600, fontSize: 13.5, padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontFamily: "inherit" };
const btnDarkStyle: React.CSSProperties = { background: "#0E1A1A", color: "#fff", border: 0, fontWeight: 600, fontSize: 13.5, padding: "10px 16px 10px 18px", borderRadius: 10, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10, fontFamily: "inherit", boxShadow: "0 1px 2px rgba(0,0,0,0.08)" };
const dropdownStyle: React.CSSProperties = { position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: `1px solid ${PALETTE.line}`, borderRadius: 10, boxShadow: "0 10px 32px rgba(0,0,0,0.12)", padding: "6px 0", minWidth: 220, zIndex: 25 };
const dropdownBtnStyle: React.CSSProperties = { display: "block", width: "100%", background: "transparent", border: 0, textAlign: "left", padding: "10px 16px", fontSize: 13.5, color: PALETTE.ink, fontWeight: 500, cursor: "pointer", fontFamily: "inherit" };
const vMarkStyle: React.CSSProperties = { background: PALETTE.vela, color: "#fff", width: 20, height: 20, borderRadius: "50%", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 };